import type { ScopeNode } from "@/types/bbel-types";

/**
 * Maps a service's output sample (pruned JSON from the latest successful run — see
 * WorkflowOutputSamples server-side) into ScopeNode children for the service's `output` projection,
 * so the picker and autocomplete offer the real response shape (#5683).
 *
 * Insert paths follow how the value is addressed in BBEL: dot for identifier-safe keys, bracket
 * form for the rest (`output["content-type"]`), `[0]` to step into an array's (sampled) first
 * element. Leaves carry a type + value preview hint. The nodes hang under the OPEN `output` node —
 * discoverability only; validation strictness is unchanged (a sample is one run's shape, not a
 * contract).
 */
const IDENTIFIER = /^[A-Za-z_$][\w$]*$/;
const MAX_DEPTH = 6;
const MAX_CHILDREN = 50;
const VALUE_PREVIEW_CHARS = 30;

export function outputShapeNodes(sampleJson: string, basePath: string): ScopeNode[] {
  let root: unknown;
  try {
    root = JSON.parse(sampleJson);
  } catch {
    return [];
  }
  return childrenOf(root, basePath, 0);
}

/** Sampled response headers as leaves under the `headers` projection — names verbatim (dashes take
 *  the bracket form: `headers["x-total-count"]`), values previewed like any string leaf. */
export function headerShapeNodes(headers: Record<string, string>, basePath: string): ScopeNode[] {
  return Object.entries(headers)
    .slice(0, MAX_CHILDREN)
    .map(([name, value]) => ({
      segment: name,
      path: memberPath(basePath, name),
      label: name,
      hint: valueHint(value),
    }));
}

/** Children for a container value; [] for primitives, over-depth, or empty containers. */
function childrenOf(value: unknown, path: string, depth: number): ScopeNode[] {
  if (depth >= MAX_DEPTH || value === null || typeof value !== "object") return [];

  if (Array.isArray(value)) {
    if (value.length === 0) return [];
    // One hop into the sampled element — the server collapses arrays to a single element whose
    // keys are the union across all elements, so this is the element SHAPE, not just row 0.
    return [node("[0]", `${path}[0]`, value[0], depth + 1, "Element shape (keys merged across items)")];
  }

  return Object.entries(value)
    .slice(0, MAX_CHILDREN)
    .map(([key, member]) => node(key, memberPath(path, key), member, depth + 1));
}

function node(segment: string, path: string, value: unknown, depth: number, hint?: string): ScopeNode {
  const children = childrenOf(value, path, depth);
  return {
    segment,
    path,
    label: segment,
    hint: hint ?? valueHint(value),
    ...(children.length > 0 ? { children } : {}),
  };
}

function memberPath(parent: string, key: string): string {
  return IDENTIFIER.test(key) ? `${parent}.${key}` : `${parent}["${key.replace(/"/g, '\\"')}"]`;
}

/** Type + sample-value preview: `string · "ACME"`, `number · 42`, `array`, … */
function valueHint(value: unknown): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array · element shape sampled";
  switch (typeof value) {
    case "string": {
      const preview = value.length > VALUE_PREVIEW_CHARS ? `${value.slice(0, VALUE_PREVIEW_CHARS)}…` : value;
      return `string · "${preview}"`;
    }
    case "number":
    case "boolean":
      return `${typeof value} · ${String(value)}`;
    default:
      return "object";
  }
}
